import React, { useMemo, useState } from "react";
import { defineMessages, useIntl } from "react-intl";
import { useCookieConsentContext } from "@use-cookie-consent/react";
import { IconInfoSquareRounded } from "@tabler/icons-react";
import { useAppConfig } from "@ct-react/core";
import { useLocaleContext } from "@ct-react/locale";
import { SiteType } from "@shared/site-type";
import { Button } from "../../components/common/minimals";
import Modal, { InnerModal } from "../../components/common/modal";
import "./cookie-banner.scss";

const transDefs = defineMessages({
  title: { id: "cookie-title", defaultMessage: "Paramètres des cookies" },
  intro: {
    id: "cookie-intro",
    defaultMessage: "Les cookies nous permettent de vous garantir la meilleur expérience utilisateur possible. Vous pouvez décider quels cookies sont placés et modifier à tout moment les paramètres de vos préférences en matière de confidentialité."
  },
  firstLabel: { id: "cookie-first-type", defaultMessage: "Cookies fonctionnels" },
  firstDesc: {
    id: "cookie-first-desc",
    defaultMessage: "Ces cookies sont nécessaires pour assurer les fonctionnalités de base de ce site. Ils ne peuvent pas être désactivés."
  },
  thirdLabel: { id: "cookie-third-type", defaultMessage: "Cookies analytiques" },
  thirdDesc: {
    id: "cookie-third-desc",
    defaultMessage: "Ces cookies collectent des informations sur l'utilisation du site dans le but d'améliorer l'expérience utilisateur."
  },
  stripe: { id: "cookie-stripe-desc", defaultMessage: "Prestataire de paiement" },
  save: { id: "cookie-consent-save", defaultMessage: "Enregistrer les paramètres" },
  acceptAll: { id: "cookie-consent-all", defaultMessage: "Accepter tous les cookies" }
});

const CookieBanner = () => {

  const intl = useIntl();
  const { locale } = useLocaleContext();
  const { acceptCookies, consent } = useCookieConsentContext();
  const { options: { siteType } } = useAppConfig();
  const [ openModal, setOpenModal ] = useState<boolean>(consent.thirdParty === undefined);
  const [ formThird, setFormThird ] = useState(consent.thirdParty ?? true);

  const onAccepted = (thirdParty: boolean) => {
    (formThird != thirdParty) && setFormThird(thirdParty);
    acceptCookies({ firstParty: true, thirdParty });
    setOpenModal(false);
  }

  const footerRendering = useMemo(() => (<>
    <Button type="button"
            className="secondary bolder"
            tabIndex={-1}
            onClick={() => onAccepted(formThird)}>{intl.formatMessage(transDefs.save)}</Button>
    <Button type="button"
            className="bolder"
            onClick={() => onAccepted(true)}>{intl.formatMessage(transDefs.acceptAll)}</Button>
  </>), [ locale, formThird ]);

  return (
    <Modal closableCross={false}
           open={openModal}
           onOpenChange={setOpenModal}>

      <InnerModal.Trigger className="cookie-link">{intl.formatMessage(transDefs.title)}</InnerModal.Trigger>

      <InnerModal.Content className="cookie-inner-modal"
                          title={intl.formatMessage(transDefs.title)}
                          footer={footerRendering}>

        <p>{intl.formatMessage(transDefs.intro)}</p>

        <form className="r-form">

          <div className="one-ctype">
            <label className="rf-checkbox">
              <input type="checkbox" id="first" checked={true} disabled={true}/>
              <span className="rf-mark"/>
            </label>
            <label htmlFor="first">{intl.formatMessage(transDefs.firstLabel)}</label>
            <div className="help">
              <p>{intl.formatMessage(transDefs.firstDesc)}</p>
              {siteType === SiteType.RENTALP &&
                <ul className="help-tech">
                  <li>
                    <span className="title">Stripe</span>
                    <span className="type">{intl.formatMessage(transDefs.stripe)}</span>
                    <a href="https://stripe.com/cookie-settings" target="_blank" tabIndex={-1}>
                      <IconInfoSquareRounded/>
                    </a>
                  </li>
                </ul>
              }
            </div>
          </div>

          <div className="one-ctype">
            <label className="rf-checkbox">
              <input type="checkbox" id="third" checked={formThird} onChange={e => setFormThird(e.target.checked)}/>
              <span className="rf-mark"/>
            </label>
            <label htmlFor="third">{intl.formatMessage(transDefs.thirdLabel)}</label>
            <div className="help">
              <p>{intl.formatMessage(transDefs.thirdDesc)}</p>
              <ul className="help-tech">
                <li>
                  <span className="title">Google Analytics</span>
                  <a href="https://policies.google.com/technologies/cookies" target="_blank" tabIndex={-1}>
                    <IconInfoSquareRounded/>
                  </a>
                </li>
              </ul>
            </div>
          </div>
        </form>

      </InnerModal.Content>

    </Modal>);

};

export default CookieBanner;
